import React, { useEffect, useState } from "react";
import { Flashbar, FlashbarProps } from "@amzn/awsui-components-react";
import { useLocation } from "react-router-dom";
import { getAppTicketTracking } from "src/Ateam-BI/Templates/Utils/AppActivityUtilsAppsyncPlus";

interface AppTicketTrackingItem {
    app_id: string;
    table_name: string;
    created_timestamp: string;
    ticket_id: string;
    ticket_url: string;
}

/**
 * IncidentBanner displays real-time data pipeline failure notifications
 * for the current app. It fetches active incidents from the app_ticket_tracking
 * DynamoDB table and renders a Flashbar item per issue with Amazon orange styling.
 * Only renders on /App/{app-id} routes.
 */
const IncidentBanner: React.FC = () => {
    const [incidents, setIncidents] = useState<AppTicketTrackingItem[]>([]);
    const ln = useLocation();

    // Extract app_id from the URL path (e.g., /App/803 -> "803")
    const getAppIdFromPath = (pathname: string): string | null => {
        const match = pathname.match(/^\/App\/(\d+)/i);
        return match ? match[1] : null;
    };

    const appId = getAppIdFromPath(ln.pathname);

    const formatTimestamp = (timestamp: string): string => {
        if (!timestamp) return "Unknown time";
        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return timestamp;
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZone: "UTC",
                timeZoneName: "short",
            });
        } catch {
            return timestamp;
        }
    };

    useEffect(() => {
        if (!appId) {
            setIncidents([]);
            return;
        }

        getAppTicketTracking(
            appId,
            (response: any) => {
                const items = response?.data?.getAppTicketTracking?.items;
                if (items && Array.isArray(items) && items.length > 0) {
                    setIncidents(items);
                } else {
                    setIncidents([]);
                }
            },
            (error: any) => {
                console.error("[IncidentBanner] Error fetching app_ticket_tracking:", error);
                setIncidents([]);
            }
        );
    }, [appId]);

    if (!appId || incidents.length === 0) {
        return null;
    }

    const flashbarItems: FlashbarProps.MessageDefinition[] = incidents.map((incident, index) => ({
        type: "warning" as FlashbarProps.Type,
        id: `incident-${incident.table_name}-${index}`,
        content: (
            <span>
                <strong>Active Data Issue — {incident.table_name}</strong>
                <br />
                Pipeline failure detected at {formatTimestamp(incident.created_timestamp)}.
                <br />
                Affected application: {incident.app_id} | Tracking ticket:{" "}
                {incident.ticket_url ? (
                    <a
                        href={incident.ticket_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#16191f", textDecoration: "underline", fontWeight: "bold" }}
                    >
                        {incident.ticket_id}
                    </a>
                ) : (
                    incident.ticket_id
                )}
            </span>
        ),
    }));

    return (
        <div className="flashbar-container incident-banner-container">
            <Flashbar items={flashbarItems} />
        </div>
    );
};

export default IncidentBanner;
